/**
 * Allys Property Detail Skeleton
 *
 * Matches PropertyDetailContentAllys: Airbnb-style image gallery hero,
 * then a 2/3 + 1/3 grid with details and sticky contact sidebar.
 * Includes navigation skeleton so the nav bar is always visible.
 */

import { AllysNavSkeleton } from './AllysNavSkeleton';

export function AllysPropertyDetailSkeleton() {
  return (
    <main className="min-h-screen bg-white">
      <AllysNavSkeleton />

      {/* Gallery hero — Airbnb grid layout */}
      <section className="relative w-full pt-20 skeleton-stagger-1">
        <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 max-h-[60vh] overflow-hidden">
          <div className="md:col-span-2 md:row-span-2 bg-gray-200 aspect-4/3 md:aspect-auto skeleton-shimmer" />
          <div className="hidden md:block bg-gray-100 skeleton-shimmer" />
          <div className="hidden md:block bg-gray-100 skeleton-shimmer" />
          <div className="hidden md:block bg-gray-100 skeleton-shimmer" />
          <div className="hidden md:block bg-gray-100 skeleton-shimmer" />
        </div>
      </section>

      {/* Content area — 2/3 main + 1/3 sidebar */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-20 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-10">
            <div className="space-y-3 skeleton-stagger-2">
              <div className="h-8 w-3/4 rounded-lg bg-gray-200 skeleton-shimmer" />
              <div className="h-5 w-1/2 rounded bg-gray-100 skeleton-shimmer" />
              <div className="h-1 w-16 rounded-full bg-[#BBA339]/40 mt-4 skeleton-shimmer" />
            </div>

            <div className="pt-8 border-t border-gray-200 space-y-4 skeleton-stagger-3">
              <div className="h-6 w-40 rounded bg-gray-200 skeleton-shimmer" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-16 rounded-xl bg-gray-50 border border-gray-100 skeleton-shimmer" />
                ))}
              </div>
            </div>

            <div className="pt-8 border-t border-gray-200 space-y-3 skeleton-stagger-4">
              <div className="h-6 w-32 rounded bg-gray-200 skeleton-shimmer" />
              <div className="space-y-2">
                <div className="h-4 w-full rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-full rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-5/6 rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-3/4 rounded bg-gray-100 skeleton-shimmer" />
              </div>
            </div>

            <div className="pt-8 border-t border-gray-200 space-y-4 skeleton-stagger-5">
              <div className="h-6 w-28 rounded bg-gray-200 skeleton-shimmer" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-10 rounded-lg bg-gray-50 skeleton-shimmer" />
                ))}
              </div>
            </div>

            <div className="pt-8 border-t border-gray-200 space-y-4 skeleton-stagger-6">
              <div className="h-6 w-24 rounded bg-gray-200 skeleton-shimmer" />
              <div className="h-64 rounded-2xl bg-gray-100 skeleton-shimmer" />
            </div>
          </div>

          {/* Sidebar — sticky contact card */}
          <div className="lg:col-span-1 skeleton-stagger-3">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-2xl border border-gray-200 p-6 space-y-5 shadow-lg">
                <div className="h-8 w-2/3 rounded-lg bg-gray-200 skeleton-shimmer" />
                <div className="h-1 w-full rounded bg-gray-100 skeleton-shimmer" />
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gray-200 shrink-0 skeleton-shimmer" />
                  <div className="space-y-2 flex-1">
                    <div className="h-5 w-28 rounded bg-gray-200 skeleton-shimmer" />
                    <div className="h-3 w-20 rounded bg-gray-100 skeleton-shimmer" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-11 rounded-lg bg-gray-100 skeleton-shimmer" />
                  <div className="h-11 rounded-lg bg-gray-100 skeleton-shimmer" />
                  <div className="h-24 rounded-lg bg-gray-100 skeleton-shimmer" />
                </div>
                <div className="h-12 rounded-xl bg-[#243462]/20 skeleton-shimmer" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
